import clsx from 'clsx';
import {
    type ComponentPropsWithoutRef,
    createContext,
    type ForwardedRef,
    forwardRef,
    memo,
    type ReactNode,
    useImperativeHandle,
    useState,
} from 'react';
import { usePress } from 'react-aria';
import { type ContextValue, useContextProps } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { Checkbox } from '../Checkbox';
import { keyboardShortcutToTextArray } from '../KeyboardShortcut';
import { LoadingIndicatorIcon } from '../Loading';
import { type IconDefinition, renderIcon } from '../util';

export type MenuItemAPI = {
    /**
     * Run the menu item `onAction` and `onActionEnd`.
     */
    runAction: () => void;
};

type InternalMenuItemProps = {
    /**
     * Allow to get a ref to the Menu Item API.
     * Useful to run action.
     */
    apiRef?: React.RefObject<MenuItemAPI> | undefined;
    /**
     * Icon displayed at the left of the menu item.
     */
    icon?: IconDefinition;
    /**
     * Main text of the menu item.
     */
    children?: ReactNode;
    /**
     * Alternative text value if the node provided in children is not a string.
     */
    textValue?: string;
    /**
     * True if the menu item has the virtual focus.
     */
    isFocused?: boolean;
    /**
     * Indicates if the menu item can open a submenu and state of it.
     */
    subMenu?: 'focused' | 'open' | 'closed';
    /**
     * Index identifier used for virtualization.
     */
    'data-key'?: string;
    /**
     * Index identifier used for virtualization.
     */
    'data-index'?: number;
    /**
     * Mark the item as disabled.
     * Not focusable and not clickable.
     */
    disabled?: boolean;
    /**
     * Parent children used to display a tree in auto-completion.
     */
    parentChildren?: ReactNode;
    /**
     * Display a check at the end of the menu item.
     */
    checked?: boolean;
    /**
     * Display a subtle title below the main text.
     */
    subtitle?: React.ReactNode;
    /**
     * Alternative text value if the node provided in subtitle is not a string.
     */
    subtitleTextValue?: string;
    /**
     * Display a suffix at the end of the menu item.
     */
    suffix?: ReactNode;
    /**
     * Display a keyboard shortcut at the end of the menu item.
     */
    keyboardShortcut?: string;
    /**
     * Additional keywords for completion.
     */
    keywords?: string[];
    /**
     * Display a checkbox at the start of the menu item, reflect the "checked" property.
     */
    checkbox?: boolean;
    /**
     * Callback run before the `onAction`.
     */
    onActionStart?: () => void;
    /**
     * Action executed when the menu item is pressed or activated using keyboard.
     */
    onAction?: (event: Event) => void | Promise<void>;
    /**
     * Callback run after the `onAction`.
     */
    onActionEnd?: () => void;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children'>;

export const MenuItemContext = createContext<ContextValue<InternalMenuItemProps, HTMLDivElement>>(
    {}
);

export type MenuItemProps = Pick<
    InternalMenuItemProps,
    | 'className'
    | 'checked'
    | 'disabled'
    | 'icon'
    | 'keyboardShortcut'
    | 'keywords'
    | 'onAction'
    | 'children'
    | 'textValue'
    | 'subtitle'
    | 'subtitleTextValue'
    | 'suffix'
    | 'checkbox'
>;

export function MenuItem(props: MenuItemProps) {
    return <InternalMenuItem {...props} />;
}

export const InternalMenuItem = forwardRef(function MenuButton(
    props: InternalMenuItemProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    [props, ref] = useContextProps(props, ref, MenuItemContext);
    const {
        apiRef,
        icon: iconProp,
        className,
        isFocused,
        subMenu,
        disabled,
        parentChildren,
        children,
        checked,
        onActionStart,
        onAction,
        onActionEnd,
        suffix,
        subtitle,
        keywords,
        keyboardShortcut,
        textValue,
        subtitleTextValue,
        checkbox,
        ...rest
    } = props;
    const [isLoading, setIsLoading] = useState(false);
    const runAction = useEventCallback(() => {
        if (isLoading) {
            return;
        }

        onActionStart?.();

        const event = new Event('action', { cancelable: true, bubbles: false });
        const value = onAction?.(event);

        if (value instanceof Promise) {
            setIsLoading(true);
            value.finally(() => {
                setIsLoading(false);
                if (onAction && !event.defaultPrevented) {
                    onActionEnd?.();
                }
            });
            return;
        }

        if (onAction && !event.defaultPrevented) {
            onActionEnd?.();
        }
    });
    useImperativeHandle(apiRef, () => ({ runAction }), [runAction]);
    const { pressProps, isPressed } = usePress({
        onPress: runAction,
        isDisabled: disabled,
        preventFocusOnPress: true,
    });
    const icon = isLoading ? LoadingIndicatorIcon : iconProp;
    return (
        <div
            ref={ref}
            role="menuitem"
            tabIndex={-1}
            data-pressed={isPressed ? '' : undefined}
            className={clsx(
                'group/menu-item',
                'relative flex min-w-48 max-w-128 py-1.5 pr-3 pl-3.5',
                'cursor-pointer select-none whitespace-nowrap text-base',
                'data-[pressed=true]:bg-standout-side-active',
                'aria-disabled:cursor-default aria-disabled:opacity-disabled',
                'before:absolute before:inset-0 before:inset-x-1 before:z-0 before:block before:rounded before:content-[""]',
                isFocused
                    ? 'before:bg-[var(--menu-item-focused-bg,var(--sp-color-bg-side))]'
                    : 'before:bg-transparent',
                className
            )}
            aria-disabled={disabled}
            {...pressProps}
            {...rest}
        >
            <div className="z-[1] inline-flex min-w-0 flex-grow items-center gap-2">
                {checkbox && (
                    <Checkbox
                        size="small"
                        disabled={disabled}
                        value={checked}
                        onChange={() => {
                            runAction();
                        }}
                    />
                )}
                {icon &&
                    renderIcon(icon, {
                        size: 16,
                        className: clsx('-mt-px shrink-0', !isFocused && 'text-muted'),
                    })}
                <span className="flex min-w-0 flex-1 flex-col truncate">
                    <span className="truncate">
                        {parentChildren ? (
                            <span className="contents text-muted">
                                {parentChildren}
                                <Icon.ChevronRight className="-mt-px mx-1 size-4 shrink-0" />
                            </span>
                        ) : null}
                        {children}
                    </span>
                    {subtitle ? (
                        <span className="truncate text-muted text-ui-small">{subtitle}</span>
                    ) : null}
                </span>
                {suffix}
                {keyboardShortcut ? <Shortcut shortcut={keyboardShortcut} /> : null}
                {checked && !checkbox && (
                    <span
                        className={clsx(
                            'w-4 text-right font-medium text-[1rem] leading-5',
                            !isFocused && 'text-muted'
                        )}
                    >
                        ✓
                    </span>
                )}
                {subMenu && (
                    <span
                        className={clsx(
                            'w-4 text-right text-[0.5rem]',
                            {
                                closed: 'text-light',
                                open: 'text-muted',
                                focused: 'text-base',
                            }[subMenu]
                        )}
                    >
                        ▶
                    </span>
                )}
            </div>
        </div>
    );
});

const Shortcut = memo(function Shortcut(props: { shortcut: string }) {
    const array = keyboardShortcutToTextArray(props.shortcut);
    if (array.length === 0) {
        return null;
    }
    return (
        <span className="inline-flex gap-[0.1875rem]">
            {array.map((value, index) => (
                <kbd key={index} className="min-w-[0.625rem] bg-none p-0 font-mono text-muted">
                    {value}
                </kbd>
            ))}
        </span>
    );
});
